// SiteLayout.jsx - The frame around every customer page: the price bar, the
// header, and the footer.
//
// The price bar is not decoration. Someone deciding whether to sell wants to know
// what gold is doing today, and putting a real, live number at the top of every
// page is the cheapest trust signal we have. When a price moves it flashes bronze
// for a second, which is the only way anybody notices it is live.
//
// LEGEND
//   1. Ticker      one metal, flashing when its number changes
//   2. PriceBar    the strip, scrolling on a phone rather than truncating
//   3. Logo        the business's own logo file, as supplied
//   4. Header      desktop nav plus the mobile sheet
//   5. Footer      four columns, and the legal strip
//   6. SiteLayout  the composed frame

import { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router";
import clsx from "clsx";

import { NAV, FOOTER_NAV } from "../lib/routes.js";
import { useSpot } from "../lib/hooks.js";
import * as analytics from "../lib/analytics.js";
import { money, moneyExact } from "../lib/format.js";
import { SITE } from "../lib/seo.jsx";
import { Menu, Close, Phone, ArrowRight } from "../ui/icons.jsx";

////////// 1. TICKER //////////

function Ticker({ label, value, className }) {
  const previous = useRef(value);
  const [flash, setFlash] = useState(false);

  useEffect(() => {
    if (previous.current !== undefined && value !== undefined && previous.current !== value) {
      setFlash(true);
      const timer = setTimeout(() => setFlash(false), 1100);
      previous.current = value;
      return () => clearTimeout(timer);
    }
    previous.current = value;
    return undefined;
  }, [value]);

  return (
    <span className={clsx("inline-flex items-baseline gap-1.5 whitespace-nowrap", className)}>
      <span className="text-paper/45">{label}</span>
      <span className={clsx("text-bronze-soft font-semibold num", flash && "flash-up")}>
        {value ? money(value) : "—"}
      </span>
    </span>
  );
}

////////// 2. PRICE BAR //////////

function PriceBar() {
  const { spot } = useSpot();

  return (
    <div className="bg-ink text-paper/80 text-[12.5px]">
      <div className="shell flex items-center justify-between gap-4 h-9">
        <div className="flex items-center gap-3 sm:gap-6 min-w-0 flex-1">
          <span className="hidden md:inline text-paper/45 uppercase tracking-[0.14em] text-[10.5px] font-semibold shrink-0">
            Spot, per troy ounce
          </span>
          {/* Scrolls rather than truncates, so a small phone still reaches
              platinum instead of losing it behind the fold. */}
          <div className="flex items-center gap-4 sm:gap-5 overflow-x-auto no-scrollbar min-w-0">
            <Ticker label="Gold" value={spot?.perOunce?.gold} />
            <Ticker label="Silver" value={spot?.perOunce?.silver} />
            <Ticker label="Platinum" value={spot?.perOunce?.platinum} />
          </div>
        </div>

        <a
          href={`tel:${SITE.phone}`}
          className="hidden md:flex items-center gap-1.5 shrink-0 hover:text-paper transition-colors"
        >
          <Phone size={13} />
          (401) 315-4653
        </a>
      </div>
    </div>
  );
}

////////// 3. LOGO //////////
// The business's own logo file, public/igs-logo.png, exactly as supplied. It is
// 1700 by 560 with transparent space around the artwork, so the image is drawn
// larger than asked and pulled back by negative margins until only the artwork
// takes up room. The class sets a font size, which is the height of the artwork
// itself; every figure below is in em, so one class sizes the lot at any
// breakpoint. A replacement file means new numbers in LOGO and nothing else.
//
// max-w-none because a percentage max-width lets a replaced element shrink to
// nothing inside a flex row, and a logo that gives way first is backwards. The
// sizes at each breakpoint are chosen so a 320px phone still fits it beside the
// kit button and the menu button.

const LOGO = {
  src: "/igs-logo.png",
  width: 1700,
  height: 560,
  // Where the artwork sits inside the file, in the file's own pixels.
  art: { left: 80, top: 92, right: 1570, bottom: 454 },
};

const em = (px) => `${(px / (LOGO.art.bottom - LOGO.art.top)).toFixed(4)}em`;

const LOGO_STYLE = {
  height: em(LOGO.height),
  margin: [
    -LOGO.art.top,
    -(LOGO.width - LOGO.art.right),
    -(LOGO.height - LOGO.art.bottom),
    -LOGO.art.left,
  ].map(em).join(" "),
};

export function Logo({ className, priority = false }) {
  return (
    <img
      src={LOGO.src}
      width={LOGO.width}
      height={LOGO.height}
      alt={SITE.name}
      fetchPriority={priority ? "high" : undefined}
      draggable={false}
      style={LOGO_STYLE}
      className={clsx("block w-auto max-w-none select-none", className)}
    />
  );
}

////////// 4. HEADER //////////

function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const MOBILE_LINKS = [
    ...NAV,
    { to: "/prices", label: "Live metal prices" },
    { to: "/track", label: "Track an order" },
    { to: "/about", label: "About us" },
    { to: "/contact", label: "Contact" },
  ];

  return (
    <>
      <header className={clsx(
        "sticky top-0 z-40 bg-paper/92 backdrop-blur-sm transition-[box-shadow,border-color] duration-300",
        scrolled ? "border-b border-rule shadow-[0_1px_12px_-6px_rgb(27_26_23/0.25)]" : "border-b border-transparent"
      )}>
        <div className="shell flex items-center justify-between gap-3 sm:gap-4 xl:gap-6 h-[60px] sm:h-[68px]">
          <Link to="/" className="shrink-0 min-w-0" aria-label={`${SITE.name} home`}>
            <Logo priority className="text-[34px] xs:text-[38px] sm:text-[44px]" />
          </Link>

          {/* The desktop nav appears at lg, but at exactly 1024 the wordmark, five
              links and two buttons came to three pixels more than the shell, so the
              nav wrapped to two lines. The gaps open up again at xl. */}
          <nav className="hidden lg:flex items-center gap-5 xl:gap-7" aria-label="Main">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => clsx(
                  "text-[14.5px] transition-colors relative py-1",
                  isActive ? "text-ink font-semibold" : "text-muted hover:text-ink"
                )}
              >
                {({ isActive }) => (
                  <>
                    {item.label}
                    <span
                      className={clsx(
                        "absolute -bottom-0.5 left-0 h-px bg-bronze transition-[width] duration-300",
                        isActive ? "w-full" : "w-0"
                      )}
                    />
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <Link to="/track" className="hidden sm:inline-flex btn btn-ghost btn-sm">Track an order</Link>
            {/* The label shortens rather than the button disappearing. On a
                320px phone this is still the most useful thing in the header. */}
            <Link to="/get-a-kit" className="btn btn-primary btn-sm sm:!px-5">
              <span className="hidden xs:inline">Get a free kit</span>
              <span className="xs:hidden">Free kit</span>
            </Link>
            <button
              type="button"
              className="lg:hidden btn btn-ghost !px-2 -mr-2"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
              aria-expanded={open}
            >
              <Menu size={22} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile sheet. Full screen because a half height drawer on a phone is a
          scroll trap and nobody finds the item they wanted. */}
      {open && (
        <div className="fixed inset-0 z-50 bg-paper lg:hidden flex flex-col fade">
          <div className="shell flex items-center justify-between h-[60px] border-b border-rule shrink-0">
            <Logo className="text-[32px]" />
            <button type="button" className="btn btn-ghost !px-2 -mr-2" onClick={() => setOpen(false)} aria-label="Close menu">
              <Close size={22} />
            </button>
          </div>

          <nav className="flex-1 overflow-y-auto shell py-4" aria-label="Mobile">
            <ul className="divide-y divide-rule-soft">
              {MOBILE_LINKS.map((item, index) => (
                <li
                  key={item.to}
                  style={{ animation: `rise 0.42s var(--ease-out) ${index * 35}ms both` }}
                >
                  <Link to={item.to} className="flex items-center justify-between py-3.5 display display-sm text-ink">
                    {item.label}
                    <ArrowRight size={18} className="text-faint" />
                  </Link>
                </li>
              ))}
            </ul>

            <div className="mt-7 pb-8 space-y-2">
              <Link to="/get-a-kit" className="btn btn-primary btn-lg w-full">Get a free kit</Link>
              <a href={`tel:${SITE.phone}`} className="btn btn-quiet w-full">
                <Phone size={16} /> (401) 315-4653
              </a>
            </div>
          </nav>
        </div>
      )}
    </>
  );
}

////////// 5. FOOTER //////////

function Footer() {
  const year = new Date().getFullYear();
  const { spot } = useSpot();

  return (
    <footer className="mt-20 md:mt-24 border-t border-rule bg-surface/50">
      <div className="shell py-12 md:py-16">
        {/* The brand block is a column like any other. Spanning the whole first
            row, as it used to, left the right half of a tablet empty. */}
        <div className="grid gap-x-8 gap-y-10 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-[1.35fr_repeat(4,1fr)]">
          <div className="max-w-xs">
            <Logo className="text-[46px]" />
            {/* 2008 is when the family began trading under this name, which is
                the date that means something to a customer. The LLC is a later
                registration and is not the same fact. foundingDate in
                lib/seo.jsx says 2008 for the same reason, and the two must not
                drift. */}
            <p className="mt-3 text-[10.5px] uppercase tracking-[0.18em] text-faint font-semibold">Est. 2008</p>
            <p className="hint mt-3 leading-relaxed">
              We buy gold, silver and platinum by mail from people across the United States.
              Free insured kit, itemised offer, and everything comes back free if you say no.
            </p>
            <a href={`tel:${SITE.phone}`} className="inline-flex items-center gap-2 mt-5 text-sm font-semibold text-ink link">
              <Phone size={15} /> (401) 315-4653
            </a>
            <p className="hint mt-1">Monday to Friday, 9am to 6pm Eastern</p>
          </div>

          {FOOTER_NAV.map((column) => (
            <div key={column.title}>
              <p className="eyebrow mb-3.5">{column.title}</p>
              <ul className="space-y-2">
                {column.links.map((link) => (
                  <li key={link.to}>
                    <Link to={link.to} className="text-[14px] text-muted hover:text-ink transition-colors">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      <div className="border-t border-rule">
        <div className="shell py-5 flex flex-col sm:flex-row gap-2 sm:items-center justify-between text-[12.5px] text-faint">
          <p>&copy; {year} International Gold &amp; Silver. All rights reserved.</p>
          <p className="num">
            {spot?.perOunce?.gold
              ? `Gold ${moneyExact(spot.perOunce.gold)} per troy ounce, updated live`
              : "Metal prices update live on every page"}
          </p>
        </div>
      </div>
    </footer>
  );
}

////////// 6. LAYOUT //////////

////////// FOCUSED HEADER //////////
// The kit form is the only page on this site that is a task rather than a
// document, and on a laptop the price bar, the nav and the page heading together
// pushed the first field below the fold. Somebody filling in a form does not need
// six links to somewhere else, so the chrome comes down to the logo and the
// phone number, which is the one link a person filling in a form actually reaches
// for. Everything else is still one back button away.

function FocusedHeader() {
  return (
    <header className="border-b border-rule bg-paper">
      <div className="shell flex items-center justify-between gap-4 h-[52px]">
        <Link to="/" aria-label={`${SITE.name} home`} className="shrink-0 min-w-0">
          <Logo className="text-[32px] sm:text-[36px]" />
        </Link>
        <a href="tel:+1-401-315-4653" className="flex items-center gap-2 text-[13.5px] font-semibold text-ink hover:text-bronze-dark transition-colors">
          <Phone size={15} className="text-bronze" />
          <span className="hidden sm:inline num">(401) 315-4653</span>
          <span className="sm:hidden">Call us</span>
        </a>
      </div>
    </header>
  );
}

export default function SiteLayout() {
  const location = useLocation();

  // The container is in index.html and needs nothing from here. This only reads
  // the campaign parameters off the first URL this visitor opens, because they
  // are long gone by the time anybody fills the form in. Route changes are not
  // reported: Analytics counts them itself as the URL changes, and a
  // second page view pushed from here would count each of them twice.
  useEffect(() => { analytics.init(); }, []);

  // Every telephone number on the site, counted once from here. A dozen files
  // carry a tel: link and more will; a listener on the document catches the ones
  // nobody remembered to wire up, including the ones added later.
  useEffect(() => {
    const onClick = (event) => {
      if (event.target.closest?.('a[href^="tel:"]')) {
        analytics.trackPhoneCall(window.location.pathname);
      }
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  // The form only. The confirmation keeps the full header on purpose, because
  // the whole point of that page is to send somebody somewhere else.
  const focused = location.pathname === "/get-a-kit";

  return (
    <div className="min-h-screen flex flex-col">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:m-3 focus:px-4 focus:py-2 focus:bg-ink focus:text-paper">
        Skip to content
      </a>

      {focused ? <FocusedHeader /> : <><PriceBar /><Header /></>}

      <main id="main" className="flex-1">
        <Outlet />
      </main>

      {!focused && <Footer />}
    </div>
  );
}
